import { useState, useEffect } from "react"
import axios from "axios"

const BASE_URL = process.env.REACT_APP_API_URL

/**
 * Returns the current hostname.
 * e.g., "kaigo.dev.envsg.com" → "kaigo.dev.envsg.com"
 * Returns null if running on localhost or an IP address.
 * @returns {string|null} The hostname or null.
 */
const getDomain = () => {
    const hostname = window.location.hostname
    if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return null
    }
    return hostname
}

const BRANDING_CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes

/**
 * Builds the localStorage cache key for branding data based on domain.
 * @param {string} domain - The current domain.
 * @returns {string} The cache key.
 */
const getBrandingCacheKey = (domain) => `branding_cache_${domain}`

/**
 * Retrieves cached branding data from localStorage if it exists and is not expired.
 * @param {string} domain - The current domain.
 * @returns {object|null} The cached branding data or null if expired/missing.
 */
const getCachedBranding = (domain) => {
    try {
        const cacheKey = getBrandingCacheKey(domain)
        const cached = localStorage.getItem(cacheKey)
        if (!cached) return null

        const { data, timestamp } = JSON.parse(cached)
        if (Date.now() - timestamp > BRANDING_CACHE_TTL_MS) {
            localStorage.removeItem(cacheKey)
            return null
        }
        return data
    } catch {
        return null
    }
}

/**
 * Stores branding data in localStorage with a timestamp for TTL-based expiry.
 * @param {string} domain - The current domain.
 * @param {object} data - The branding data to cache.
 */
const setCachedBranding = (domain, data) => {
    try {
        const cacheKey = getBrandingCacheKey(domain)
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }))
    } catch {
        // localStorage full or unavailable — silently ignore
    }
}

/**
 * Fetches branding configuration (logo, colors) for the current domain.
 * Uses localStorage cache (keyed by domain) with a 10-minute TTL to avoid
 * excessive API calls against the rate-limited branding endpoint.
 * @returns {{ branding: object|null, loading: boolean, subdomain: string|null }}
 */
const useLoginBranding = () => {
    const domain = getDomain()
    const cachedData = domain ? getCachedBranding(domain) : null

    const [branding, setBranding] = useState(cachedData)
    const [loading, setLoading] = useState(!cachedData)

    useEffect(() => {
        if (!domain) {
            setLoading(false)
            return
        }

        // If we already have valid cached data, skip the API call
        if (cachedData) {
            return
        }

        const fetchBranding = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/v1/fleet-manager/branding`, {
                    params: {
                        domain, // Sends full hostname (e.g. kaigo.dev.envsg.com)
                    },
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    timeout: 10000,
                })

                if (response.data?.status && response.data?.data) {
                    setBranding(response.data.data)
                    setCachedBranding(domain, response.data.data)
                }
            } catch (error) {
                console.error("Failed to fetch login branding:", error)
            } finally {
                setLoading(false)
            }
        }

        fetchBranding()
    }, [domain, cachedData])

    return { branding, loading, domain }
}

export default useLoginBranding
